import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import React, { useCallback, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { KybSubmissionData } from 'apis/legitimizer/types';
import { Typography as T } from 'common/typography';

import { BusinessRepresentativeInfo } from './BusinessRepresentativeInfo';
import { InfoSubRow } from './InfoSubRow';
import MissingFieldError from './MissingFieldError';
import { SectionConfigurationContext } from '../SectionConfigurationContext';
import { legalFormOptions } from '../mappings';
import { useBasicInfoFieldsConfigMemo } from '../useFieldsConfigMemo';

type Props = {
  submissionData: KybSubmissionData;
  onEditData: (section: string) => void;
  setHasMissingFields?: (value: boolean) => void;
};

const Content = styled.div`
  text-align: start;
`;

const Legend = styled.legend`
  padding: 24px 0;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SecondaryAction = styled(Button).attrs({
  size: 'small',
  variant: 'lightBlue',
})``;

const ViewRow = FormRow;

const ReviewBasicInfo = ({ submissionData, onEditData, setHasMissingFields }: Props) => {
  const sectionConfiguration = useContext(SectionConfigurationContext);
  const intl = useIntl();
  const basicInfoFieldsConfig = useBasicInfoFieldsConfigMemo({ sectionConfiguration, entityUUID: true });

  const displayAddress =
    basicInfoFieldsConfig.streetAddress.visible ||
    basicInfoFieldsConfig.postalCode.visible ||
    basicInfoFieldsConfig.city.visible ||
    basicInfoFieldsConfig.countryCode.visible;

  const businessRepresentatives = submissionData.basicCompanyInfo?.businessRepresentatives ?? [];

  const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);

  const section = 'basic-info';
  const businessDetailsConfig = sectionConfiguration[section]?.businessDetails ?? [];
  if (!sectionConfiguration.enabledSections.includes(section) || businessDetailsConfig.length === 0) {
    return null;
  }

  const resolveLegalFormValue = (value: string | null | undefined) => {
    const key = legalFormOptions.find(option => option.value === value)?.translationKey;
    return key ? t(key) : value;
  };

  const isVatLiable = submissionData.basicCompanyInfo?.businessDetails?.isVatLiable?.value ?? false;
  const isVatLiableMessageResultId = isVatLiable
    ? 'compliance.kyb.sections.basic-info.business-information.is-vat-liable-option.yes'
    : 'compliance.kyb.sections.basic-info.business-information.is-vat-liable-option.no';

  return (
    <Content>
      <Legend>
        <T.Heading6>
          <FormattedMessage id="compliance.kyb.sections.basic-info.business-information.title" />
        </T.Heading6>
        <SecondaryAction type="button" onClick={() => onEditData('basic-info')}>
          <FormattedMessage id="state.edit" />
        </SecondaryAction>
      </Legend>

      {basicInfoFieldsConfig.companyName.visible && (
        <ViewRow label={t('compliance.kyb.sections.basic-info.business-information.name')}>
          {submissionData.basicCompanyInfo?.businessDetails?.companyName?.value || (
            <MissingFieldError
              shouldShow={basicInfoFieldsConfig.companyName.required}
              hasFieldErrorState={setHasMissingFields}
            />
          )}
        </ViewRow>
      )}

      {displayAddress && (
        <ViewRow label={t('compliance.kyb.sections.basic-info.business-information.address')}>
          {basicInfoFieldsConfig.streetAddress.visible && (
            <InfoSubRow title={t('compliance.kyb.address.street-address')}>
              {submissionData.basicCompanyInfo?.businessDetails?.streetAddress?.value || (
                <MissingFieldError
                  shouldShow={basicInfoFieldsConfig.streetAddress.required}
                  hasFieldErrorState={setHasMissingFields}
                />
              )}
            </InfoSubRow>
          )}
          {(basicInfoFieldsConfig.postalCode.visible || basicInfoFieldsConfig.city.visible) && (
            <>
              <InfoSubRow title={t('compliance.kyb.address.postal-code')}>
                {(basicInfoFieldsConfig.postalCode.visible &&
                  submissionData.basicCompanyInfo?.businessDetails?.postalCode?.value) || (
                  <MissingFieldError
                    shouldShow={basicInfoFieldsConfig.postalCode.required}
                    hasFieldErrorState={setHasMissingFields}
                  />
                )}{' '}
                &nbsp;
              </InfoSubRow>
              <InfoSubRow title={t('compliance.kyb.address.city')}>
                {(basicInfoFieldsConfig.city.visible &&
                  submissionData.basicCompanyInfo?.businessDetails?.city?.value) || (
                  <MissingFieldError
                    shouldShow={basicInfoFieldsConfig.city.required}
                    hasFieldErrorState={setHasMissingFields}
                  />
                )}
              </InfoSubRow>
            </>
          )}
          {basicInfoFieldsConfig.countryCode.visible && (
            <InfoSubRow title={t('compliance.kyb.address.country')}>
              {submissionData.basicCompanyInfo?.businessDetails?.countryCode?.value || (
                <MissingFieldError
                  shouldShow={basicInfoFieldsConfig.countryCode.required}
                  hasFieldErrorState={setHasMissingFields}
                />
              )}
            </InfoSubRow>
          )}
        </ViewRow>
      )}

      {basicInfoFieldsConfig.registrationNumber.visible && (
        <ViewRow label={t('compliance.kyb.sections.basic-info.business-information.business-id')}>
          {submissionData.basicCompanyInfo?.businessDetails?.registrationNumber?.value || (
            <MissingFieldError
              shouldShow={basicInfoFieldsConfig.registrationNumber.required}
              hasFieldErrorState={setHasMissingFields}
            />
          )}
        </ViewRow>
      )}

      {basicInfoFieldsConfig.isVatLiable.visible && (
        <ViewRow label={t('compliance.kyb.sections.basic-info.business-information.is-vat-liable')}>
          <FormattedMessage id={isVatLiableMessageResultId} />
        </ViewRow>
      )}

      {basicInfoFieldsConfig.vatId.visible && (
        <ViewRow label={t('compliance.kyb.sections.basic-info.business-information.vat-id')}>
          {submissionData.basicCompanyInfo?.businessDetails?.vatId?.value || (
            <MissingFieldError
              shouldShow={basicInfoFieldsConfig.vatId.required || isVatLiable}
              hasFieldErrorState={setHasMissingFields}
            />
          )}
        </ViewRow>
      )}

      {basicInfoFieldsConfig.tin.visible && (
        <ViewRow label={t('compliance.kyb.sections.basic-info.business-information.tin')}>
          {submissionData.basicCompanyInfo?.businessDetails?.tin?.value || (
            <MissingFieldError
              shouldShow={basicInfoFieldsConfig.tin.required}
              hasFieldErrorState={setHasMissingFields}
            />
          )}
        </ViewRow>
      )}

      {basicInfoFieldsConfig.lucidId.visible && (
        <ViewRow label={t('compliance.kyb.sections.basic-info.business-information.lucid-id.name')}>
          {submissionData.basicCompanyInfo?.businessDetails?.lucidId?.value || (
            <MissingFieldError
              shouldShow={basicInfoFieldsConfig.lucidId.required}
              hasFieldErrorState={setHasMissingFields}
            />
          )}
        </ViewRow>
      )}

      {basicInfoFieldsConfig.legalForm.visible && (
        <ViewRow label={t('compliance.kyb.sections.basic-info.business-information.legal-form')}>
          {resolveLegalFormValue(submissionData.basicCompanyInfo?.businessDetails?.legalForm?.value) || (
            <MissingFieldError
              shouldShow={basicInfoFieldsConfig.legalForm.required}
              hasFieldErrorState={setHasMissingFields}
            />
          )}
        </ViewRow>
      )}

      {basicInfoFieldsConfig.dateOfRegistration.visible && (
        <ViewRow label={t('compliance.kyb.sections.basic-info.business-information.registration-date')}>
          {submissionData.basicCompanyInfo?.businessDetails?.dateOfRegistration?.value || (
            <MissingFieldError
              shouldShow={basicInfoFieldsConfig.dateOfRegistration.required}
              hasFieldErrorState={setHasMissingFields}
            />
          )}
        </ViewRow>
      )}

      {basicInfoFieldsConfig.taxCountryCode.visible && (
        <ViewRow label={t('compliance.kyb.sections.basic-info.business-information.country-of-taxation')}>
          {submissionData.basicCompanyInfo?.businessDetails?.taxCountryCode?.value || (
            <MissingFieldError
              shouldShow={basicInfoFieldsConfig.taxCountryCode.required}
              hasFieldErrorState={setHasMissingFields}
            />
          )}
        </ViewRow>
      )}

      {basicInfoFieldsConfig.fieldOfIndustry.visible && (
        <ViewRow label={t('compliance.kyb.sections.basic-info.business-information.field-of-industry')}>
          {submissionData.basicCompanyInfo?.businessDetails?.fieldOfIndustry?.value || (
            <MissingFieldError
              shouldShow={basicInfoFieldsConfig.fieldOfIndustry.required}
              hasFieldErrorState={setHasMissingFields}
            />
          )}
        </ViewRow>
      )}

      {basicInfoFieldsConfig.phone.visible && (
        <ViewRow label={t('compliance.kyb.sections.basic-info.business-information.phone-number')}>
          {submissionData.basicCompanyInfo?.businessDetails?.phoneNumber?.value || (
            <MissingFieldError
              shouldShow={basicInfoFieldsConfig.phone.required}
              hasFieldErrorState={setHasMissingFields}
            />
          )}
        </ViewRow>
      )}

      {basicInfoFieldsConfig.businessRepresentatives.visible && (
        <ViewRow label={t('compliance.kyb.sections.basic-info.company-representatives.title')}>
          <MissingFieldError
            shouldShow={basicInfoFieldsConfig.businessRepresentatives.required && businessRepresentatives.length === 0}
            hasFieldErrorState={setHasMissingFields}
          />
          {businessRepresentatives.map(businessRepresentative => (
            <BusinessRepresentativeInfo
              key={businessRepresentative.entityUUID}
              businessRepresentative={businessRepresentative}
              sectionConfiguration={sectionConfiguration}
            />
          ))}
        </ViewRow>
      )}
    </Content>
  );
};

export default ReviewBasicInfo;
